/**
 * GroupedList — a virtualized, full-width list whose rows are organized under
 * section header bands (e.g. "High Priority" / "Medium priority").
 *
 * This is deliberately NOT a table: there is no column model, no column header
 * row, and no cells — each row is a caller-rendered, full-width piece of
 * content. Reach for `DataTable` when you have genuinely columnar data; reach
 * for `GroupedList` when you have a sectioned feed of rows.
 *
 * Pure layout primitive:
 * - Owns the section header band (label + a `trailing` slot for caller
 *   controls such as bulk actions), row chrome (divider, hover highlight,
 *   click/keyboard activation), optional always-visible selection checkboxes
 *   with shift-click range support, and optional hover-revealed row actions.
 * - Does NOT know what the items are. Row content, header trailing controls,
 *   and hover actions are all supplied by the caller via render props.
 * - Virtualizes with @tanstack/react-virtual (headers and rows are flattened
 *   into a single virtual list), so it handles hundreds of rows.
 *
 * Selection is controlled: pass `selectable`, `selectedIds`, and
 * `onSelectionChange`. Shift-click range selection (anchor = last toggled row,
 * range in visual order across sections) is handled internally.
 */

import {
  useVirtualizer,
  type VirtualItem,
  type Virtualizer,
} from '@tanstack/react-virtual';
import {
  type KeyboardEvent,
  memo,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { CheckboxBase } from '@/lib/base/forms/checkbox';
import { cx } from '@/utils/cx';

export interface GroupedListSection<T> {
  /** Stable unique section id (e.g. 'high', 'crm_updates'). */
  id: string;
  /**
   * Header band content — typically a short label string. Omit to render the
   * section's rows with no header band (an ungrouped/flat section).
   */
  label?: ReactNode;
  /** Optional header-band controls, right-aligned (e.g. bulk action buttons). */
  trailing?: ReactNode;
  /** Rows in this section, in display order. */
  items: T[];
}

export interface GroupedListProps<T> {
  sections: GroupedListSection<T>[];
  /** Stable unique id per item — used for keys, selection, and data-row-id. */
  getItemId: (item: T) => string;
  /** Full-width row content. The row shell (divider, hover, click) is owned by GroupedList. */
  renderItem: (item: T) => ReactNode;
  /** Row activation (click / Enter / Space). Rows are keyboard-focusable when provided. */
  onItemClick?: (item: T) => void;
  /** Accessible name for a row. Recommended when onItemClick is set. */
  getItemLabel?: (item: T) => string;

  /** Opt-in selection: renders an always-visible checkbox at the start of each row. */
  selectable?: boolean;
  /** Controlled selection state (item ids). Required when selectable. */
  selectedIds?: ReadonlySet<string>;
  /** Receives the next selection. Shift-click ranges are resolved internally. */
  onSelectionChange?: (next: Set<string>) => void;

  /**
   * Opt-in hover actions: rendered at the end of the row, revealed on row
   * hover or when any action receives keyboard focus (focus-within). Hidden
   * via opacity, not unmounted, so they remain in the tab order and the
   * accessibility tree.
   */
  renderItemActions?: (item: T) => ReactNode;

  /**
   * Called after render with the items currently materialized by the
   * virtualizer (visible + overscan). Use for visibility-driven work such as
   * prefetching row details. May fire with the same items on unrelated
   * re-renders — callers must dedupe.
   */
  onVisibleItemsChange?: (visible: T[]) => void;

  /** Virtualizer height estimates (px). Rows are re-measured after render. */
  estimatedItemHeight?: number;
  estimatedHeaderHeight?: number;

  /**
   * Where to start, for lists that unmount and remount — e.g. a route that
   * swaps the list for a detail view and back. Read ONCE, at mount: it seeds
   * the virtualizer's own initial offset, so later changes to the prop are
   * ignored and can never yank the user back. Row-anchored rather than a raw
   * pixel offset on purpose: a remounted virtualized list knows only its
   * height ESTIMATES for the rows above the viewport, so the same pixel
   * offset lands on a different row — measured one row off after a short
   * scroll, and worse the deeper the user was. If the anchored row is gone
   * (resolved while the user was away) its old index is used instead. Omit
   * for lists that always start at the top.
   */
  initialScrollAnchor?: GroupedListScrollAnchor;

  /**
   * Called as the user scrolls, with the row now at the top of the viewport.
   * Pair with `initialScrollAnchor` to persist and restore position across
   * unmount. Fires on animation frames while scrolling, so keep the handler
   * cheap — writing to a ref or a module-level map is the intended use, not
   * setting React state.
   */
  onScrollAnchorChange?: (anchor: GroupedListScrollAnchor) => void;

  /**
   * Rendered inside the scroll container, after the virtualized rows. Use
   * for end-of-list content that must scroll with the rows — infinite-load
   * sentinels, "loading more" indicators, end-of-window captions.
   */
  footer?: ReactNode;

  'aria-label'?: string;
  className?: string;
}

/**
 * A scroll position expressed as "this row, this far past its top". Rows are
 * identified by key so the position survives the list re-measuring, and by
 * index as a fallback for when the row itself has gone.
 */
export interface GroupedListScrollAnchor {
  /** Key of the first row in view: an item's id, or `header-<sectionId>`. */
  rowKey: string;
  /** That row's index in the flattened list, used if `rowKey` is not found. */
  index: number;
  /** Pixels the viewport was scrolled past the top of that row. */
  offset: number;
}

type FlatRow<T> =
  | { type: 'header'; section: GroupedListSection<T> }
  | { type: 'item'; item: T; id: string };

/** Stable identity for a flattened row — the React key and the anchor key. */
const getRowKey = <T,>(row: FlatRow<T>): string =>
  row.type === 'header' ? `header-${row.section.id}` : row.id;

// Measured in-browser against the AI Actions row (py-[16px] + a 20px line
// + 1px border-b): 54px. The previous 44 predated the row's current padding
// — a wrong estimate only costs accuracy before measurement, but it is also
// the value stale-cache bugs surface as, so keep it truthful.
const DEFAULT_ITEM_HEIGHT = 54;
// Measured in-browser: header row is `min-h-8` (32px) after the header
// className dropped `min-h-[40px] py-2` in favor of `h-8`-style padding.
const DEFAULT_HEADER_HEIGHT = 32;

/** Clicks on hover actions must never bubble into the row's onItemClick. */
const stopClickPropagation = (e: MouseEvent) => {
  e.stopPropagation();
};

interface GroupedListRowProps<T> {
  item: T;
  id: string;
  renderItem: (item: T) => ReactNode;
  onItemClick?: (item: T) => void;
  label?: string;
  selectable: boolean;
  isChecked: boolean;
  onToggle?: (id: string, shiftKey: boolean) => void;
  actions?: ReactNode;
}

function GroupedListRowInner<T>({
  item,
  id,
  renderItem,
  onItemClick,
  label,
  selectable,
  isChecked,
  onToggle,
  actions,
}: GroupedListRowProps<T>) {
  const handleClick = useCallback(() => {
    onItemClick?.(item);
  }, [onItemClick, item]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      // Only activate on the row itself — Enter/Space inside a nested
      // control (checkbox, hover action button) must not open the row.
      if (e.target !== e.currentTarget) {
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onItemClick?.(item);
      }
    },
    [onItemClick, item]
  );

  const handleCheckboxMouseDown = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    // Shift+click is range selection — suppress the browser's shift-click
    // text-selection behavior, which starts on mousedown.
    if (e.shiftKey) {
      e.preventDefault();
    }
  }, []);

  const handleCheckboxClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onToggle?.(id, e.shiftKey);
    },
    [onToggle, id]
  );

  const interactive = onItemClick !== undefined;

  return (
    <div
      data-row-id={id}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={label}
      onClick={interactive ? handleClick : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
      className={cx(
        'group/row relative flex w-full items-center gap-2 pl-4 pr-4',
        'border-b border-[var(--color-border-secondary)] bg-bg-primary transition-colors duration-75',
        interactive &&
          'cursor-pointer hover:bg-bg-secondary outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-brand-600)]',
        // Non-interactive rows still highlight on hover when they carry hover
        // actions — the highlight is what reveals the action affordance.
        !interactive && actions !== undefined && 'hover:bg-bg-secondary'
      )}
    >
      {selectable && (
        <button
          type='button'
          className='flex-shrink-0 flex items-center self-stretch'
          aria-label={
            isChecked
              ? `Deselect ${label ?? 'row'}`
              : `Select ${label ?? 'row'}`
          }
          aria-pressed={isChecked}
          onMouseDown={handleCheckboxMouseDown}
          onClick={handleCheckboxClick}
        >
          <CheckboxBase isSelected={isChecked} />
        </button>
      )}
      <div className='min-w-0 flex-1'>{renderItem(item)}</div>
      {actions !== undefined && (
        <div
          onClick={stopClickPropagation}
          className={cx(
            'absolute right-3 top-1/2 -translate-y-1/2 z-10 flex items-center gap-1',
            'opacity-0 transition-opacity duration-75',
            'group-hover/row:opacity-100 focus-within:opacity-100'
          )}
        >
          {actions}
        </div>
      )}
    </div>
  );
}

const GroupedListRow = memo(GroupedListRowInner) as typeof GroupedListRowInner;

export function GroupedList<T>({
  sections,
  getItemId,
  renderItem,
  onItemClick,
  getItemLabel,
  selectable = false,
  selectedIds,
  onSelectionChange,
  renderItemActions,
  onVisibleItemsChange,
  estimatedItemHeight = DEFAULT_ITEM_HEIGHT,
  estimatedHeaderHeight = DEFAULT_HEADER_HEIGHT,
  initialScrollAnchor,
  onScrollAnchorChange,
  footer,
  'aria-label': ariaLabel,
  className,
}: GroupedListProps<T>) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Anchor for shift-click range selection: the id of the last row whose
  // checkbox was toggled without shift.
  const selectionAnchorRef = useRef<string | null>(null);

  const flatRows = useMemo<FlatRow<T>[]>(() => {
    const rows: FlatRow<T>[] = [];
    for (const section of sections) {
      if (section.items.length === 0) {
        continue;
      }
      if (section.label !== undefined) {
        rows.push({ type: 'header', section });
      }
      for (const item of section.items) {
        rows.push({ type: 'item', item, id: getItemId(item) });
      }
    }
    return rows;
  }, [sections, getItemId]);

  /** Item ids in visual order — the coordinate system for shift ranges. */
  const orderedItemIds = useMemo(
    () => flatRows.flatMap(row => (row.type === 'item' ? [row.id] : [])),
    [flatRows]
  );

  const handleToggle = useCallback(
    (id: string, shiftKey: boolean) => {
      if (!onSelectionChange) {
        return;
      }
      const current = selectedIds ?? new Set<string>();
      const next = new Set(current);
      const anchor = selectionAnchorRef.current;

      if (shiftKey && anchor !== null && anchor !== id) {
        const anchorIdx = orderedItemIds.indexOf(anchor);
        const targetIdx = orderedItemIds.indexOf(id);
        if (anchorIdx !== -1 && targetIdx !== -1) {
          const [from, to] =
            anchorIdx < targetIdx
              ? [anchorIdx, targetIdx]
              : [targetIdx, anchorIdx];
          // The anchor's current state decides whether the range selects or
          // deselects, so shift-click extends whatever the anchor did.
          const selecting = current.has(anchor);
          for (let i = from; i <= to; i++) {
            const rangeId = orderedItemIds[i];
            if (selecting) {
              next.add(rangeId);
            } else {
              next.delete(rangeId);
            }
          }
          onSelectionChange(next);
          return;
        }
      }

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      selectionAnchorRef.current = id;
      onSelectionChange(next);
    },
    [onSelectionChange, selectedIds, orderedItemIds]
  );

  const getScrollElement = useCallback(() => scrollRef.current, []);

  const estimateSize = useCallback(
    (index: number) =>
      flatRows[index].type === 'header'
        ? estimatedHeaderHeight
        : estimatedItemHeight,
    [flatRows, estimatedHeaderHeight, estimatedItemHeight]
  );

  const getItemKey = useCallback(
    (index: number) => getRowKey(flatRows[index]),
    [flatRows]
  );

  // Index of the row a restore is anchored on, from the moment the anchor is
  // resolved until that row has been measured (or the user scrolls). See
  // `shouldAdjustScrollPositionOnItemSizeChange` below for why it matters.
  const restoreAnchorIndexRef = useRef<number | null>(null);

  // Resolve the saved anchor to a pixel offset in the virtualizer's OWN
  // starting coordinate system — every row above at its estimate, exactly as
  // the virtualizer lays them out before anything is measured. Scrolling
  // there puts the anchored row at the top of the viewport; as rows above it
  // measure taller than their estimates the virtualizer shifts the offset by
  // the difference (its item-size correction), so the row stays pinned.
  const getInitialOffset = useCallback(() => {
    if (!initialScrollAnchor || flatRows.length === 0) {
      return 0;
    }
    let index = flatRows.findIndex(
      row => getRowKey(row) === initialScrollAnchor.rowKey
    );
    if (index === -1) {
      index = Math.min(initialScrollAnchor.index, flatRows.length - 1);
    }
    let start = 0;
    for (let i = 0; i < index; i++) {
      start += estimateSize(i);
    }
    restoreAnchorIndexRef.current = index;
    // The offset into the row is capped at the row's ESTIMATE, not its real
    // height (which is not known yet): any spill past the estimate would put
    // the next row's estimated start above the viewport edge, and its growth
    // would then be treated as "above" and shift the viewport.
    const offset = Math.min(
      initialScrollAnchor.offset,
      Math.max(0, estimateSize(index) - 1)
    );
    return start + offset;
  }, [initialScrollAnchor, flatRows, estimateSize]);

  // The virtualizer's default keeps the viewport stable when a row above it
  // is measured: it shifts scrollTop by the row's growth. "Above" means the
  // row's TOP is above the viewport edge — which includes the row straddling
  // that edge. During a restore that row is the anchor, and applying its own
  // growth would push it up and out: measured in a browser as landing one
  // row below the saved one every time. So while a restore is pending the
  // anchored row's measurement is exempt and rows are classified by index
  // (the anchor is the top row, so "above" is exactly "earlier"). Installed
  // on the instance only for that window — see below — so the library
  // default, whose inputs are private, applies at every other time.
  const adjustScrollDuringRestore = useCallback(
    (
      item: VirtualItem,
      _delta: number,
      instance: Virtualizer<HTMLDivElement, Element>
    ) => {
      const anchorIndex = restoreAnchorIndexRef.current;
      if (anchorIndex === null) {
        // The window closed since the last render (the anchor measured, or
        // the user scrolled); approximate the default until the next render
        // reinstates it.
        return item.start < (instance.scrollOffset ?? 0);
      }
      if (item.index === anchorIndex) {
        restoreAnchorIndexRef.current = null;
        return false;
      }
      return item.index < anchorIndex;
    },
    []
  );

  const virtualizer = useVirtualizer({
    count: flatRows.length,
    getScrollElement,
    estimateSize,
    // Key measurements by the STABLE row key, not the index (the default).
    // Without this, the measurement cache is positional: when the row list
    // shifts — a WebSocket arrival, a band boundary moving, a re-sort — a
    // measurement taken for "index 5 = 54px item row" keeps applying after
    // index 5 becomes a 32px band header, while React's keyed reconciliation
    // keeps the same elements mounted so neither ref re-attach nor
    // ResizeObserver ever corrects it. Measured live: that mismatch overlapped
    // a band header 10px onto the row above it, painting over the row's 1px
    // bottom border — the "separator lines sometimes disappear" bug.
    getItemKey,
    overscan: 5,
    // The saved position goes through the virtualizer, NOT a scrollTop write
    // of our own. When the virtualizer attaches to the scroll element it calls
    // `scrollTo(<its offset>)` — and it attaches TWICE under React StrictMode
    // (mount, simulated unmount, mount again). A layout-effect restore of ours
    // ran between the two, so the second attach scrolled straight back to the
    // default offset of 0 and the feature silently did nothing; traced in a
    // browser before this was rewritten. Seeding `initialOffset` makes both
    // attaches scroll to the saved position, and the very first render already
    // materializes the rows around it, so nothing ever paints at the top. The
    // virtualizer evaluates this once, on its first render — later renders
    // pass a fresh closure it never calls.
    initialOffset: getInitialOffset,
  });

  // Report the row at the top of the viewport as the user scrolls so callers
  // can persist it. Coalesced onto an animation frame: scroll fires far more
  // often than a caller needs, and an unthrottled handler on a virtualized
  // list is a measurable cost. The rows and the virtualizer are read through
  // refs so the listener attaches once: re-attaching on a render would cancel
  // a report still waiting on its frame.
  const flatRowsRef = useRef(flatRows);
  flatRowsRef.current = flatRows;
  const virtualizerRef = useRef(virtualizer);
  virtualizerRef.current = virtualizer;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    let frame = 0;
    const handleScroll = () => {
      // Once the user scrolls, the restore is over: the anchored row gets the
      // default measurement treatment like any other row from here on.
      restoreAnchorIndexRef.current = null;
      if (!onScrollAnchorChange || frame) {
        return;
      }
      frame = requestAnimationFrame(() => {
        frame = 0;
        const { scrollTop } = el;
        const rows = flatRowsRef.current;
        // The first materialized row that extends past the top edge is the
        // one the user sees at the top.
        const items = virtualizerRef.current.getVirtualItems();
        const top = items.find(v => v.end > scrollTop) ?? items[0];
        const row = top ? rows[top.index] : undefined;
        if (!top || !row) {
          return;
        }
        onScrollAnchorChange({
          rowKey: getRowKey(row),
          index: top.index,
          offset: Math.max(0, scrollTop - top.start),
        });
      });
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [onScrollAnchorChange]);

  // Report the items the virtualizer currently materializes (visible +
  // overscan) so callers can do visibility-driven work (e.g. prefetching).
  // The virtual-items array gets a new identity per render, so this fires on
  // unrelated re-renders too — the contract requires callers to dedupe.
  const virtualItems = virtualizer.getVirtualItems();
  // Must follow getVirtualItems(): on the first render that call is what
  // resolves the anchor (via initialOffset) and opens the restore window.
  virtualizer.shouldAdjustScrollPositionOnItemSizeChange =
    restoreAnchorIndexRef.current === null
      ? undefined
      : adjustScrollDuringRestore;
  useEffect(() => {
    if (!onVisibleItemsChange) {
      return;
    }
    const visible: T[] = [];
    for (const vItem of virtualItems) {
      const row = flatRows[vItem.index];
      if (row?.type === 'item') {
        visible.push(row.item);
      }
    }
    onVisibleItemsChange(visible);
  }, [onVisibleItemsChange, virtualItems, flatRows]);

  return (
    <div
      ref={scrollRef}
      data-component='grouped-list'
      aria-label={ariaLabel}
      className={cx('h-full w-full overflow-y-auto', className)}
    >
      <div
        className='relative w-full'
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualItems.map(vItem => {
          const row = flatRows[vItem.index];
          return (
            <div
              key={getRowKey(row)}
              ref={virtualizer.measureElement}
              data-index={vItem.index}
              className='absolute top-0 left-0 w-full'
              style={{ transform: `translateY(${vItem.start}px)` }}
            >
              {row.type === 'header' ? (
                // Measured in-browser: 32px (`min-h-8`) replaces the old
                // `min-h-[40px] py-2`, hugging the label at the new size. A
                // floor, not a fixed height, so taller content (larger font,
                // a future trailing badge) can still grow instead of clipping.
                <div
                  data-component='grouped-list-header'
                  data-instance={row.section.id}
                  className='flex min-h-8 items-center justify-between gap-2 px-4 bg-bg-secondary border-b border-[var(--color-border-secondary)]'
                >
                  <span className='text-xs font-semibold text-[var(--color-text-primary)] whitespace-nowrap'>
                    {row.section.label}
                  </span>
                  {row.section.trailing !== undefined && (
                    <div className='flex items-center gap-2'>
                      {row.section.trailing}
                    </div>
                  )}
                </div>
              ) : (
                <GroupedListRow
                  item={row.item}
                  id={row.id}
                  renderItem={renderItem}
                  onItemClick={onItemClick}
                  label={getItemLabel?.(row.item)}
                  selectable={selectable}
                  isChecked={selectedIds?.has(row.id) ?? false}
                  onToggle={selectable ? handleToggle : undefined}
                  actions={renderItemActions?.(row.item)}
                />
              )}
            </div>
          );
        })}
      </div>
      {footer}
    </div>
  );
}
GroupedList.displayName = 'GroupedList';
